import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { SpecFieldGroup, specFields, type SpecField } from './SpecFields';
import { MarkdownDisplay } from '../shared/MarkdownDisplay';
import { SchemaLabelsExplainer } from '../shared/ConceptExplainers';
import { useAccountSlug } from '@/contexts/AuthContext';
import { toFriendlyBuildError } from '@/utils/composerErrors';
import { endpointApiPath } from '@/utils/endpointUrl';
import type { ComposerBuildResult, ComposerBranch, ComposerChatMessage, ComposerExternalActions, ComposerQuestion, ComposerSpec } from '@/types/composer';

interface ComposerMessageProps {
  message: ComposerChatMessage;
  /**
   * True only for the newest message in the stream. The latest turn's options and
   * questions are rendered by the interaction area between the stream and the
   * input (the retired ComposerRunner, task 1117) — not here. Past turns show them as muted static text,
   * so history is visibly frozen and the User never thinks editing an old answer
   * would change the conversation.
   */
  isLatest: boolean;
}

/**
 * A single chat turn. Human messages are right-aligned indigo bubbles; assistant
 * replies are flat left-aligned markdown with no bounding box — only artifacts
 * (SpecPreview, ResultCard) carry borders.
 */
export default function ComposerMessage({ message, isLatest }: ComposerMessageProps) {
  const isHuman = message.role === 'human';

  return (
    <div className={`flex ${isHuman ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[85%] ${isHuman ? 'items-end' : 'items-start'} flex flex-col gap-2`}>
        {isHuman ? (
          <div className="rounded-2xl rounded-br-sm bg-indigo-600 px-4 py-2.5 text-sm text-white whitespace-pre-wrap break-words">
            {message.text}
          </div>
        ) : (
          <div className="flex gap-2 text-sm text-gray-900 dark:text-gray-100">
            <span className="shrink-0">✨</span>
            <div className="min-w-0 break-words">
              <MarkdownDisplay content={message.text} bare />
            </div>
          </div>
        )}

        {/* Past turns only: frozen, non-interactive rendering of the turn's
            affordances. The latest turn's live form/chips render in the
            interaction area between the stream and the input. */}
        {!isLatest && message.questions && message.questions.length > 0 && (
          <QuestionListStatic questions={message.questions} />
        )}

        {!isLatest && message.options && message.options.length > 0 && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {message.options.map((opt) => opt.label).join(' · ')}
          </p>
        )}

        {message.buildResult && <ResultCard result={message.buildResult} />}
      </div>
    </div>
  );
}

/**
 * Serialize filled answers into the single chat message the guide reads back:
 * one `Label: answer` line per answered question (trailing "?" dropped so the
 * line reads naturally). Unanswered optional questions are simply omitted.
 */
const serializeAnswers = (
  questions: ComposerQuestion[],
  answers: Record<string, string>
): string =>
  questions
    .map((q) => {
      const raw = answers[q.id]?.trim();
      if (!raw) return null;
      const display =
        q.type === 'choice'
          ? q.options?.find((o) => o.value === raw)?.label ?? raw
          : q.type === 'boolean'
          ? raw === 'yes'
            ? 'Yes'
            : 'No'
          : raw;
      return `${q.label.replace(/\?\s*$/, '')}: ${display}`;
    })
    .filter(Boolean)
    .join('\n');

/**
 * Interactive answer form for the guide's structured questions — rendered by the
 * Assistant conversation's interaction area while its turn is the latest. One "Send
 * answers" action serializes everything into a single chat message via the normal
 * send path; the answers then live on as the human bubble that submission creates.
 */
export function QuestionForm({
  questions,
  onSubmit,
  disabled,
}: {
  questions: ComposerQuestion[];
  onSubmit: (text: string) => void;
  disabled: boolean;
}) {
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const setAnswer = (id: string, value: string) =>
    setAnswers((prev) => ({ ...prev, [id]: value }));

  const missingRequired = questions.some((q) => q.required && !answers[q.id]?.trim());
  const nothingAnswered = questions.every((q) => !answers[q.id]?.trim());
  const serialized = serializeAnswers(questions, answers);

  const choiceChip = (q: ComposerQuestion, value: string, label: string) => (
    <button
      key={value}
      type="button"
      disabled={disabled}
      onClick={() => setAnswer(q.id, answers[q.id] === value ? '' : value)}
      aria-pressed={answers[q.id] === value}
      className={`px-3 py-1.5 text-sm rounded-full border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
        answers[q.id] === value
          ? 'border-indigo-600 bg-indigo-600 text-white'
          : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:border-indigo-300 dark:hover:border-indigo-700'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="w-full rounded-lg border border-indigo-200 dark:border-indigo-800 bg-indigo-50/50 dark:bg-indigo-900/10 p-4 space-y-4">
      {questions.map((q) => (
        <div key={q.id} className="space-y-1.5">
          <label
            htmlFor={`composer-q-${q.id}`}
            className="block text-sm font-medium text-gray-800 dark:text-gray-200"
          >
            {q.label}
            {q.required && <span className="ml-0.5 text-indigo-500">*</span>}
          </label>
          {q.type === 'text' && (
            <input
              id={`composer-q-${q.id}`}
              type="text"
              value={answers[q.id] ?? ''}
              onChange={(e) => setAnswer(q.id, e.target.value)}
              placeholder={q.placeholder}
              disabled={disabled}
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-60"
            />
          )}
          {q.type === 'choice' && (
            <div className="flex flex-wrap gap-2">
              {(q.options ?? []).map((opt) => choiceChip(q, opt.value, opt.label))}
            </div>
          )}
          {q.type === 'boolean' && (
            <div className="flex gap-2">
              {choiceChip(q, 'yes', 'Yes')}
              {choiceChip(q, 'no', 'No')}
            </div>
          )}
        </div>
      ))}

      <button
        type="button"
        disabled={disabled || missingRequired || nothingAnswered || !serialized}
        onClick={() => onSubmit(serialized)}
        className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Send answers
      </button>
    </div>
  );
}

/**
 * Static rendering of a past turn's questions — plain text, no inputs, so history
 * is visibly immutable. The answers appear in the human bubble that followed.
 */
function QuestionListStatic({ questions }: { questions: ComposerQuestion[] }) {
  return (
    <ul className="list-disc list-inside space-y-0.5 text-sm text-gray-500 dark:text-gray-400">
      {questions.map((q) => (
        <li key={q.id}>{q.label}</li>
      ))}
    </ul>
  );
}

/** Terminal build outcome: "Try it" + the created resources tucked under the hood, or the failure reason. */
/**
 * The applied-result card. Exported (task 720) so the Assistant sidebar renders
 * the SAME card the Composer page does — the specialist stays the single
 * implementation of its capability, and its result should not have two looks.
 */
export function ResultCard({ result }: { result: ComposerBuildResult }) {
  const navigate = useNavigate();

  if (result.status === 'failed') {
    // `result.error` is free text the builder agent wrote, so it can carry a
    // raw "HTTP 401 Unauthorized …" or a "[402] no_active_plan" it saw in a tool
    // result. Translate the credential case (task 395) and the plan refusals
    // (task 1026) into copy the User can act on — a plan refusal is not a
    // problem with the design, so it must never invite "tell me what to change".
    // Everything else shows verbatim so nothing is hidden.
    const friendly = toFriendlyBuildError(result.error);
    return (
      <div className="w-full rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4">
        <p className="text-sm font-semibold text-red-800 dark:text-red-200">That didn't work</p>
        <p className="mt-1 text-sm text-red-700 dark:text-red-300">
          {friendly?.message || result.error || "I couldn't finish composing it. Nothing was left behind."}
        </p>
        <p className="mt-2 text-xs text-red-600 dark:text-red-400">
          {friendly ? friendly.footer : 'Tell me what to change and we can try again.'}
        </p>
        {friendly?.linkTo && (
          <Link
            to={friendly.linkTo}
            data-testid="build-error-remedy-link"
            className="mt-3 inline-flex items-center rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
          >
            {friendly.linkLabel}
          </Link>
        )}
      </div>
    );
  }

  const ids = result.resourceIds || {};
  const isConversational = result.conversational === true;
  // The service composes the full slug-qualified URL for us (task 385) — show
  // it verbatim. `endpointPath` alone carries no account slug, so it is only a
  // last-resort fallback for responses from an older build.
  const callableUrl = result.endpointUrl ?? `/api/${result.endpointPath}`;
  const links: { label: string; to: string }[] = [];

  // Branching builds: router + per-branch + converge
  if (ids.routerAgentId) {
    links.push({ label: 'Router capability', to: `/agents/${ids.routerAgentId}` });
  }
  if (ids.routerPromptId) {
    links.push({ label: 'Router instructions', to: `/prompts/${ids.routerPromptId}` });
  }
  if (ids.branchAgentIds?.length) {
    ids.branchAgentIds.forEach((id, i) => links.push({ label: `Route ${i + 1} capability`, to: `/agents/${id}` }));
  }
  if (ids.branchPromptIds?.length) {
    ids.branchPromptIds.forEach((id, i) => links.push({ label: `Route ${i + 1} instructions`, to: `/prompts/${id}` }));
  }
  if (ids.convergeAgentId) {
    links.push({ label: 'Converge capability', to: `/agents/${ids.convergeAgentId}` });
  }
  if (ids.convergePromptId) {
    links.push({ label: 'Converge instructions', to: `/prompts/${ids.convergePromptId}` });
  }

  // Multi-step builds: one entry per stage
  if (!ids.routerAgentId) {
    if (ids.promptIds?.length) {
      ids.promptIds.forEach((id, i) => links.push({ label: `Stage ${i + 1} instructions`, to: `/prompts/${id}` }));
    } else if (ids.promptId) {
      links.push({ label: 'Instructions', to: `/prompts/${ids.promptId}` });
    }
    if (ids.agentIds?.length) {
      ids.agentIds.forEach((id, i) => links.push({ label: `Stage ${i + 1} capability`, to: `/agents/${id}` }));
    } else if (ids.agentId) {
      links.push({ label: 'Capability', to: `/agents/${ids.agentId}` });
    }
  }

  if (ids.configId) links.push({ label: 'Collaboration', to: `/configs/${ids.configId}` });
  if (ids.endpointId) links.push({ label: 'AI Service', to: `/services/${ids.endpointId}` });

  return (
    <div className="w-full rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-4">
      <p className="text-sm font-semibold text-green-800 dark:text-green-200">
        ✨ {isConversational ? 'Your chat assistant is ready' : 'Your AI Service is ready'}
      </p>
      {(result.endpointUrl || result.endpointPath) && (
        <p className="mt-1 text-sm text-green-700 dark:text-green-300">
          {isConversational
            ? <>Chat with it at <code className="font-mono">POST {callableUrl}</code> — pass <code className="font-mono">collaborationId</code> to continue the conversation.</>
            : <>Use it anytime at <code className="font-mono">POST {callableUrl}</code></>
          }
        </p>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {ids.endpointId && isConversational && result.endpointPath && (
          // Conversational: open the endpoint hub's Test tab, which already
          // supports multi-turn via collaborationId continuation.
          <button
            type="button"
            onClick={() => navigate(`/services/${ids.endpointId}?tab=test`)}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700"
          >
            Try it — start chatting
          </button>
        )}
        {ids.endpointId && !isConversational && (
          <Link
            to={`/services/${ids.endpointId}?tab=test`}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700"
          >
            Try it
          </Link>
        )}
        {/* The service's own page, on Overview (task 871). The Assistant carries
            a User who built this from the Dashboard here on its own; from every
            other route this link is how they get here, so it is on the card
            rather than buried in the disclosure below. */}
        {ids.endpointId && (
          <Link
            to={`/services/${ids.endpointId}`}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md border border-green-300 dark:border-green-700 text-green-800 dark:text-green-200 bg-white dark:bg-gray-800 hover:bg-green-50 dark:hover:bg-gray-700"
          >
            Open service
          </Link>
        )}
      </div>

      {links.length > 0 && (
        <details className="mt-3 group">
          <summary className="cursor-pointer text-xs text-green-700 dark:text-green-400 hover:underline select-none">
            Under the hood — see what was built
          </summary>
          <div className="mt-2 flex flex-wrap gap-2">
            {links.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

const LLM_LABELS: Record<string, string> = {
  'anthropic-default': 'Balanced',
  'anthropic-fast': 'Quick & economical',
  'openai-default': 'Alternative',
};

/**
 * Live, plain-language preview of what Composer will build. Outcome-first: it
 * never names the internal primitives (agent/prompt/schema/endpoint). The raw
 * shapes live under an "Under the hood" disclosure for the curious. Read-only —
 * the backend contract accepts only a chat `message`, so the user refines by
 * talking to Composer ("call the input field transcript instead").
 */
export function SpecPreview({
  spec,
  /**
   * The panel's eyebrow. Defaults to the CREATE wording, which is what the
   * Composer page and a fresh build conversation both want; the Assistant
   * sidebar passes change wording on a modify turn (task 804), where "Here's
   * what I'll compose" described the wrong operation on an existing service.
   */
  heading = "Here's what I'll compose",
}: {
  spec: ComposerSpec;
  heading?: string;
}) {
  // Create-time preview: the endpoint does not exist yet, so the URL is shown
  // under the CALLER's own account slug (task 385). Once built, the result card
  // switches to the service-composed `buildResult.endpointUrl`.
  const callerSlug = useAccountSlug();
  const steps = spec.steps && spec.steps.length > 0 ? spec.steps : null;
  const branches = spec.branches && spec.branches.length > 0 ? spec.branches : null;
  const isConversational = spec.conversational === true;
  const externalActions = spec.externalActions;
  const hasExternalActions =
    externalActions &&
    (externalActions.description ||
      (externalActions.registryTools && externalActions.registryTools.length > 0) ||
      (externalActions.mcpConnections && externalActions.mcpConnections.length > 0));

  // Input comes from the first step/branch/agent; output from the last step/converge/agent.
  // Conversational assistants use a fixed message/response contract.
  // Task 703: the panel renders these as typed chips, so it needs the field
  // types alongside the names. Same sources as before — only the shape changed.
  const inputs: SpecField[] = isConversational
    ? [{ name: 'message', type: 'string', required: true }]
    : branches
    ? specFields(branches[0]?.input)
    : steps
    ? specFields(steps[0]?.input)
    : specFields(spec.agent?.input);
  const outputs: SpecField[] = isConversational
    ? [{ name: 'response', type: 'string', required: true }]
    : branches
    ? specFields(spec.converge?.output)
    : steps
    ? specFields(steps[steps.length - 1]?.output)
    : specFields(spec.agent?.output);

  const hasContent =
    spec.intent || inputs.length || outputs.length || spec.endpoint?.path || steps || branches || isConversational || hasExternalActions;
  if (!hasContent) return null;

  return (
    /* `spec-panel` establishes the container-query context (src/index.css): the
       panel's internals reflow on ITS width, not the viewport's, because this
       same component renders both as a half-screen column and as a narrow
       stacked mount above the composer input (task 703). */
    <div className="spec-panel rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">
        {heading}
      </p>
      <div className="text-sm">
        {spec.intent && <Row label="What it does" value={spec.intent} divider={false} />}

        {/* Conversational: show it clearly as a chat assistant */}
        {isConversational && (
          <Row
            label="How it works"
            value="A chat assistant your team can have ongoing conversations with — send a message and it replies in context."
          />
        )}

        {/* Branching: show the routes legibly and jargon-free */}
        {!isConversational && branches && branches.length > 1 && (
          <BranchingRoutesSummary branches={branches} />
        )}

        {/* Multi-step sequence — one SHORT line per stage. The full `intent`
            prose lives only in the under-the-hood stage cards now, so it is
            written once instead of twice (task 703). */}
        {!isConversational && steps && steps.length > 1 && (
          <Section label="How it works">
            <ol className="mt-0.5 space-y-2">
              {steps.map((step, i) => (
                <li key={i} className="flex items-baseline gap-2.5">
                  <span
                    aria-hidden="true"
                    className="inline-flex h-[18px] w-[18px] shrink-0 translate-y-0.5 items-center justify-center rounded-full bg-indigo-100 text-[10.5px] font-semibold text-indigo-700 dark:bg-indigo-900 dark:text-indigo-200"
                  >
                    {i + 1}
                  </span>
                  <span className="min-w-0 line-clamp-1 text-gray-900 dark:text-gray-100">
                    {stageSummary(step, i)}
                  </span>
                </li>
              ))}
            </ol>
          </Section>
        )}

        {/* Needs / gives sit side by side once the PANEL is wide enough. */}
        {!isConversational && (inputs.length > 0 || outputs.length > 0) && (
          <div className="spec-duo">
            {inputs.length > 0 && (
              <Section label="Needs from you">
                <FieldChips fields={inputs} />
              </Section>
            )}
            {outputs.length > 0 && (
              <Section label="Gives back">
                <FieldChips fields={outputs} />
              </Section>
            )}
          </div>
        )}

        {isConversational && spec.assistant?.purpose && (
          <Row label="Its purpose" value={spec.assistant.purpose} />
        )}
        {hasExternalActions && externalActions?.description && (
          <Row label="What it reaches out to" value={externalActions.description} />
        )}
        {spec.endpoint?.path && (
          <Section label="How you'll use it">
            <EndpointRow
              method={spec.endpoint.method || 'POST'}
              path={endpointApiPath({ path: spec.endpoint.path, accountSlug: callerSlug })}
            />
          </Section>
        )}
      </div>

      {/* First contact with the "needs from you / gives back" labels (task 539). */}
      {!isConversational && (inputs.length > 0 || outputs.length > 0) && (
        <SchemaLabelsExplainer className="mt-3" />
      )}

      {/* Detail, not the headline contract — closed by default so the panel's
          common case is roughly half as tall (task 703). */}
      <details className="group mt-3 border-t border-gray-200 pt-2.5 dark:border-gray-700" data-testid="spec-under-the-hood">
        <summary className="flex cursor-pointer select-none list-none items-center gap-1.5 text-xs text-gray-500 hover:underline dark:text-gray-400 [&::-webkit-details-marker]:hidden">
          <ChevronRightIcon
            aria-hidden="true"
            className="h-3 w-3 shrink-0 transition-transform group-open:rotate-90"
          />
          Under the hood
        </summary>
        <div className="mt-2 space-y-3">
          {spec.llmConfigName && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              How it thinks: {LLM_LABELS[spec.llmConfigName] || spec.llmConfigName}
            </p>
          )}
          {hasExternalActions && externalActions && (
            <ExternalActionsDetail externalActions={externalActions} />
          )}
          {isConversational
            ? (
              <div className="space-y-1 border-l-2 border-indigo-200 dark:border-indigo-700 pl-3">
                <p className="text-xs font-medium text-gray-600 dark:text-gray-300">
                  Multi-turn conversational assistant (simple-chat shape)
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Takes: <span className="font-mono">message</span> (string) — Returns: <span className="font-mono">response</span> (string)
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Entry node + agent node + loop-back edge — continuation via <span className="font-mono">collaborationId</span>
                </p>
                {spec.assistant?.whatItKnows && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Knows: {spec.assistant.whatItKnows}
                  </p>
                )}
              </div>
            )
            : branches
            ? (
              /* Stage cards lay out two-up once the panel is wide enough. */
              <div className="spec-stage-grid">
                {branches.map((branch, i) => (
                  <div key={i} className="min-w-0 border-l-2 border-indigo-200 pl-3 dark:border-indigo-700">
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-300">
                      {branch.label || branch.name || `Route ${i + 1}`}
                      {branch.condition === null ? ' (default)' : ''}
                    </p>
                    {branch.intent && (
                      <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{branch.intent}</p>
                    )}
                    <SpecFieldGroup label="Gives" schema={branch.output as Record<string, unknown>} />
                  </div>
                ))}
              </div>
            )
            : steps
            ? (
              <div className="spec-stage-grid">
                {steps.map((step, i) => (
                  <div key={i} className="min-w-0 border-l-2 border-indigo-200 pl-3 dark:border-indigo-700">
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-300">
                      Stage {i + 1}
                      {step.name ? ` — ${step.name}` : ''}
                    </p>
                    {/* The full prose lives HERE only — the summary above shows
                        the short name (task 703). */}
                    {step.intent && (
                      <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{step.intent}</p>
                    )}
                    <SpecFieldGroup label="Takes" schema={step.input as Record<string, unknown>} />
                    <SpecFieldGroup label="Gives" schema={step.output as Record<string, unknown>} />
                  </div>
                ))}
              </div>
            )
            : (
              <div className="spec-duo">
                <SpecFieldGroup label="Inputs" schema={spec.agent?.input as Record<string, unknown>} />
                <SpecFieldGroup label="Outputs" schema={spec.agent?.output as Record<string, unknown>} />
              </div>
            )}
        </div>
      </details>

      {isConversational && (
        <div className="mt-3 flex items-start gap-2 rounded-md bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-700 px-3 py-2">
          <span className="text-indigo-600 dark:text-indigo-400 text-sm leading-none mt-0.5">💬</span>
          <p className="text-xs text-indigo-700 dark:text-indigo-300">
            This is a conversational assistant — it remembers the thread across messages.
          </p>
        </div>
      )}

      {hasExternalActions && (
        <div className="mt-3 flex items-start gap-2 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 px-3 py-2">
          <span className="text-amber-600 dark:text-amber-400 text-sm leading-none mt-0.5">↗</span>
          <p className="text-xs text-amber-700 dark:text-amber-300">
            This capability reaches external systems to do its work.
          </p>
        </div>
      )}

      <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
        Want a change? Just tell me in the chat.
      </p>
    </div>
  );
}

/** Jargon-free summary of a branching capability's routes, e.g.
 *  "Sorts into: Refund / Complaint / Other — each handled its own way" */
function BranchingRoutesSummary({ branches }: { branches: ComposerBranch[] }) {
  const labels = branches.map((b, i) => b.label || b.name || `Route ${i + 1}`);
  return (
    <Section label="How it routes">
      <span>Sorts into: </span>
      <span className="font-medium">{labels.join(' / ')}</span>
      <span> — each handled its own way</span>
    </Section>
  );
}

/**
 * One labelled block of the contract: a small uppercase eyebrow above its value
 * on its own line (task 703).
 *
 * This replaces a fixed 160px label column that left values only ~160px of a
 * 360px rail. Stacking roughly doubles the measure at the same panel width, and
 * it is what lets the panel stay readable in the narrow stacked mount.
 */
function Section({
  label,
  children,
  divider = true,
}: {
  label: string;
  children: React.ReactNode;
  divider?: boolean;
}) {
  return (
    <div
      className={
        divider
          ? 'border-t border-gray-100 dark:border-gray-700/60 pt-3 mt-3'
          : undefined
      }
    >
      <p className="mb-1 text-[10.5px] font-semibold uppercase tracking-[0.07em] text-gray-500 dark:text-gray-400">
        {label}
      </p>
      <div className="min-w-0 text-gray-900 dark:text-gray-100">{children}</div>
    </div>
  );
}

/** A plain text value under an eyebrow. */
function Row({
  label,
  value,
  divider = true,
}: {
  label: string;
  value: string;
  divider?: boolean;
}) {
  return (
    <Section label={label} divider={divider}>
      {value}
    </Section>
  );
}

/**
 * Field names as mono chips with an inline type hint, replacing a comma-joined
 * sentence. Chips wrap on whitespace between them, so nothing breaks mid-word.
 */
function FieldChips({ fields }: { fields: SpecField[] }) {
  return (
    <div className="mt-0.5 flex flex-wrap gap-1.5" data-testid="spec-field-chips">
      {fields.map((field) => (
        <span
          key={field.name}
          className="inline-flex items-center rounded-full border border-gray-300 bg-gray-50 px-2.5 py-0.5 font-mono text-[11px] text-gray-800 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200"
        >
          {field.name}
          {field.type && (
            <span className="ml-1.5 font-sans text-[10.5px] text-blue-600 dark:text-blue-300">
              {field.type}
            </span>
          )}
        </span>
      ))}
    </div>
  );
}

/**
 * The call: a method badge plus the path on ONE horizontally scrollable line.
 *
 * The old row used `break-all`, which split the URL mid-word
 * ("/api/innovate-o" / "wner/..."). Scrolling keeps the path readable and
 * copyable at any panel width.
 */
function EndpointRow({ method, path }: { method: string; path: string }) {
  return (
    <div className="mt-0.5 flex min-w-0 items-center gap-2">
      <span className="shrink-0 rounded bg-green-100 px-2 py-0.5 text-[10.5px] font-bold tracking-wide text-green-800 dark:bg-green-900/50 dark:text-green-300">
        {method}
      </span>
      <code
        data-testid="spec-endpoint-path"
        className="min-w-0 flex-1 overflow-x-auto whitespace-nowrap rounded border border-gray-200 bg-gray-50 px-2.5 py-1 font-mono text-xs text-gray-800 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200"
      >
        {path}
      </code>
    </div>
  );
}

/**
 * The one-line summary for a stage in "How it works".
 *
 * Prefers the stage's short `name`; falls back to its `intent`, which the
 * caller clamps to a single line. The full prose stays in the stage's
 * under-the-hood card so it appears exactly once (task 703).
 */
function stageSummary(step: { name?: string; intent?: string }, index: number): string {
  const name = step.name?.trim();
  if (name) return name;
  const intent = step.intent?.trim();
  if (intent) return intent;
  return `Stage ${index + 1}`;
}

/** Technical detail about external tools and MCP connections, shown inside "Under the hood". */
function ExternalActionsDetail({ externalActions }: { externalActions: ComposerExternalActions }) {
  const tools = externalActions.registryTools ?? [];
  const mcps = externalActions.mcpConnections ?? [];
  if (tools.length === 0 && mcps.length === 0) return null;
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-gray-600 dark:text-gray-300">Reaches external systems</p>
      {tools.length > 0 && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Built-in tools: <span className="font-mono">{tools.join(', ')}</span>
        </p>
      )}
      {mcps.length > 0 && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Connections: <span className="font-mono">{mcps.join(', ')}</span>
        </p>
      )}
    </div>
  );
}
